// components/AssetLibraryPanel.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { 
  FiArrowLeft, FiPlus, FiImage, FiX, FiTrash2, FiDownload, FiSettings, FiArrowRight, FiChevronDown
} from 'react-icons/fi';
import { FaRegTrashCan } from 'react-icons/fa6';
import { TbCube3dSphere, TbFileExport } from 'react-icons/tb';
import { BiFileBlank } from 'react-icons/bi';
import { getStorage, ref, uploadString, getDownloadURL, deleteObject } from 'firebase/storage';
import { collection, addDoc, deleteDoc, doc, getDocs, query, where, orderBy, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { SkeletonShimmer } from './SkeletonComponents.jsx';
import { 
  calculateAssetUploadCost, 
  checkSufficientCredits, 
  deductImageCredits,
  getRemainingCredits
} from '../services/usageService';

// ============================================
// HELPER FUNCTIONS
// ============================================

// Format file size
const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Get asset icon
const getAssetIcon = (asset) => {
  if (asset.type === '3d') return <TbCube3dSphere size={24} />;
  if (asset.type === 'image') return <FiImage size={24} />;
  return <BiFileBlank size={24} />;
};

// Allowed file types
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const ALLOWED_3D_TYPES = ['.obj', '.gltf', '.glb'];

// ============================================
// MINI 3D VIEWER COMPONENT
// ============================================
const MiniModelViewer = ({ assetUrl, fileExtension, onClose }) => {
  const mountRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const sceneRef = useRef(null);
  const modelRef = useRef(null);
  const controlsRef = useRef(null);
  const rendererRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isDisposedRef = useRef(false);

  const cleanupWebGL = useCallback(() => {
    isDisposedRef.current = true;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (controlsRef.current) {
      controlsRef.current.dispose();
      controlsRef.current = null;
    }

    if (sceneRef.current) {
      sceneRef.current.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(m => {
              if (m.map) m.map.dispose();
              if (m.normalMap) m.normalMap.dispose();
              m.dispose();
            });
          } else {
            if (child.material.map) child.material.map.dispose();
            if (child.material.normalMap) child.material.normalMap.dispose();
            child.material.dispose();
          }
        }
      });
      sceneRef.current.clear();
      sceneRef.current = null;
    }

    if (rendererRef.current) {
      rendererRef.current.forceContextLoss();
      rendererRef.current.dispose();
      rendererRef.current = null;
    }

    if (mountRef.current) {
      while (mountRef.current.firstChild) {
        const child = mountRef.current.firstChild;
        if (child.tagName === 'CANVAS') {
          const gl = child.getContext('webgl') || child.getContext('webgl2');
          if (gl) {
            gl.getExtension('WEBGL_lose_context')?.loseContext();
          }
        }
        mountRef.current.removeChild(child);
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    isDisposedRef.current = false;

    const initViewer = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const THREE = await import('three');
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls');

        if (!isMounted || !mountRef.current) return;

        cleanupWebGL();

        const scene = new THREE.Scene();
        scene.background = new THREE.Color('#2a2a2a');
        sceneRef.current = scene;

        const container = mountRef.current;
        const width = container.clientWidth || 300;
        const height = container.clientHeight || 250;

        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
        camera.position.set(3, 2, 4);
        camera.lookAt(0, 0, 0);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        rendererRef.current = renderer;
        renderer.setSize(width, height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.shadowMap.enabled = true;
        container.appendChild(renderer.domElement);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
        scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
        directionalLight.position.set(5, 10, 5);
        directionalLight.castShadow = true;
        scene.add(directionalLight);

        const fillLight = new THREE.DirectionalLight(0x88b7ff, 0.3);
        fillLight.position.set(-3, 1, -2);
        scene.add(fillLight);

        const groundGeometry = new THREE.PlaneGeometry(10, 10);
        const groundMaterial = new THREE.MeshStandardMaterial({
          color: '#333333',
          roughness: 0.8,
          transparent: true,
          opacity: 0.3
        });
        const ground = new THREE.Mesh(groundGeometry, groundMaterial);
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -1;
        scene.add(ground);

        const gridHelper = new THREE.GridHelper(6, 20, '#555555', '#333333');
        scene.add(gridHelper);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.target.set(0, 0.5, 0);
        controls.minDistance = 1.5;
        controls.maxDistance = 8;
        controls.maxPolarAngle = Math.PI / 2;
        controls.update();
        controlsRef.current = controls;

        // Load model
        try {
          const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader');
          const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader');

          const ext = fileExtension?.toLowerCase();
          let model;

          const urlParts = assetUrl.split('/o/');
          let storagePath = '';
          let blobUrl = '';

          if (urlParts.length > 1) {
            storagePath = decodeURIComponent(urlParts[1].split('?')[0]);
          }

          let blob;
          try {
            const { getStorage, ref: storageRef, getBlob } = await import('firebase/storage');
            const storage = getStorage();
            const fileRef = storageRef(storage, storagePath);
            blob = await getBlob(fileRef);
            blobUrl = URL.createObjectURL(blob);
          } catch (firebaseError) {
            console.warn('Firebase SDK fetch failed:', firebaseError);
            const response = await fetch(assetUrl, { mode: 'cors', credentials: 'omit' });
            if (response.ok) {
              blob = await response.blob();
              blobUrl = URL.createObjectURL(blob);
            } else {
              throw new Error('Fetch failed');
            }
          }

          if (ext === '.gltf' || ext === '.glb') {
            const loader = new GLTFLoader();
            const gltf = await new Promise((resolve, reject) => {
              loader.load(blobUrl, resolve, undefined, reject);
            });
            model = gltf.scene;
          } else if (ext === '.obj') {
            const loader = new OBJLoader();
            model = await new Promise((resolve, reject) => {
              loader.load(blobUrl, resolve, undefined, reject);
            });
          } else {
            throw new Error('Unsupported file format');
          }

          URL.revokeObjectURL(blobUrl);

          if (!isMounted) return;

          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 2 / maxDim;

          model.scale.setScalar(scale);
          model.position.set(-center.x * scale, -center.y * scale + 0.5, -center.z * scale);

          model.traverse((child) => {
            if (child.isMesh && !child.material) {
              child.material = new THREE.MeshStandardMaterial({
                color: '#88b7ff',
                roughness: 0.5,
                metalness: 0.1
              });
            }
            if (child.isMesh) {
              child.castShadow = true;
              child.receiveShadow = true;
            }
          });

          scene.add(model);
          modelRef.current = model;

          if (isMounted) setIsLoading(false);
        } catch (loadError) {
          console.error('Error loading 3D model:', loadError);
          if (isMounted) {
            setError('Failed to load 3D model. The file may be in an unsupported format.');
            setIsLoading(false);
          }
        }

        const animate = () => {
          if (!isMounted || isDisposedRef.current || !renderer) return;
          animationFrameRef.current = requestAnimationFrame(animate);

          if (controlsRef.current) {
            controlsRef.current.update();
          }

          renderer.render(scene, camera);
        };

        animationFrameRef.current = requestAnimationFrame(animate);

        const handleResize = () => {
          if (!container || !renderer || !camera) return;
          const w = container.clientWidth;
          const h = container.clientHeight;
          camera.aspect = w / h;
          camera.updateProjectionMatrix();
          renderer.setSize(w, h);
        };

        window.addEventListener('resize', handleResize);

        return () => {
          window.removeEventListener('resize', handleResize);
          cleanupWebGL();
        };

      } catch (err) {
        console.error('Error initializing 3D viewer:', err);
        if (isMounted) {
          setError('Failed to initialize 3D viewer');
          setIsLoading(false);
        }
      }
    };

    initViewer();

    return () => {
      isMounted = false;
      cleanupWebGL();
    };
  }, [assetUrl, fileExtension, cleanupWebGL]);

  return (
    <div style={{
      width: '100%',
      height: '100%',
      position: 'relative',
      borderRadius: '8px',
      overflow: 'hidden',
      background: '#2a2a2a'
    }}>
      <div
        ref={mountRef}
        style={{ width: '100%', height: '100%', minHeight: '200px' }}
      />
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(42, 42, 42, 0.9)',
          zIndex: 10,
        }}>
          <div style={{ textAlign: 'center', color: '#ccc', fontFamily: 'Avenir, sans-serif' }}>
            <TbCube3dSphere size={32} style={{ marginBottom: '8px', animation: 'spin 2s linear infinite' }} />
            <p style={{ fontSize: '0.8rem', margin: 0 }}>Loading 3D Model...</p>
          </div>
        </div>
      )}
      {error && (
        <div style={{
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(42, 42, 42, 0.9)',
          zIndex: 10,
        }}>
          <div style={{ textAlign: 'center', color: '#e02f1d', fontFamily: 'Avenir, sans-serif' }}>
            <TbCube3dSphere size={32} style={{ marginBottom: '8px' }} />
            <p style={{ fontSize: '0.8rem', margin: 0 }}>{error}</p>
          </div>
        </div>
      )}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (onClose) onClose();
        }}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          background: 'rgba(0,0,0,0.6)',
          border: 'none',
          borderRadius: '50%',
          width: '24px',
          height: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          color: 'white',
          zIndex: 20,
          fontSize: '0.7rem',
        }}
        title="Close 3D Viewer"
      >
        <FiX size={14} />
      </button>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// ============================================
// LAZY IMAGE COMPONENT
// ============================================
const LazyImage = ({ src, alt, style, className, ...props }) => {
  const imgRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const element = imgRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const handleLoad = () => setIsLoaded(true);
  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
  };

  return (
    <div
      ref={imgRef}
      style={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: '100%',
      }}
      className={className}
    >
      {!isLoaded && isInView && (
        <SkeletonShimmer width="100%" height="100%" borderRadius="0" />
      )}
      {isInView && !hasError && (
        <img
          src={src}
          alt={alt}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            width: '100%',
            height: '100%',
            objectFit: style?.objectFit || 'cover',
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease',
            ...style,
          }}
          {...props}
        />
      )}
      {hasError && (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#e0e0e0',
          color: '#999',
          fontSize: '0.8rem',
          fontFamily: 'Avenir, sans-serif',
        }}>
          <FiImage size={24} style={{ opacity: 0.5 }} />
        </div>
      )}
    </div>
  );
};

// ============================================
// MAIN ASSET LIBRARY PANEL COMPONENT
// ============================================
const AssetLibraryPanel = ({ 
  authUser, 
  onClose,
  userAssets: externalUserAssets,
  setUserAssets: externalSetUserAssets,
  onAddToProject,
  isPWA,
  imagesRemaining,
  videosRemaining,
  updateRemainingCounts
}) => {
  // Local state for assets (can use external or internal)
  const [userAssets, setUserAssets] = useState(externalUserAssets || []);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetDetails, setShowAssetDetails] = useState(false);
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteAssetPopup, setShowDeleteAssetPopup] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'image', '3d', 'textures'
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showMiniViewer, setShowMiniViewer] = useState(false);
  const [miniViewerAsset, setMiniViewerAsset] = useState(null);
  const [userData, setUserData] = useState(null);
  
  // Generated textures from DevStudio
  const [generatedTextures, setGeneratedTextures] = useState([]);
  const [texturesLoading, setTexturesLoading] = useState(false);
  
  // Refs
  const assetFileInputRef = useRef(null);

  // ============================================
  // FETCH USER ASSETS
  // ============================================
  const fetchUserAssets = useCallback(async () => {
    if (!authUser) return;
    
    setAssetsLoading(true);
    try {
      const assetsRef = collection(db, 'assets');
      const q = query(
        assetsRef,
        where('userId', '==', authUser.uid),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const assets = [];
      querySnapshot.forEach((docSnap) => {
        assets.push({ id: docSnap.id, ...docSnap.data() });
      });
      setUserAssets(assets);
      if (externalSetUserAssets) externalSetUserAssets(assets);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setAssetsLoading(false);
    }
  }, [authUser, externalSetUserAssets]);

    // ============================================
  // FETCH GENERATED TEXTURES FROM DEVSTUDIO
  // ============================================
  const fetchGeneratedTextures = useCallback(async () => {
    if (!authUser) return;
    
    setTexturesLoading(true);
    try {
      const texturesRef = collection(db, 'marketing_images');
      const q = query(
        texturesRef,
        where('userId', '==', authUser.uid),
        where('type', '==', 'texture'),
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const textures = [];
      querySnapshot.forEach((docSnap) => {
        textures.push({ 
          id: docSnap.id, 
          ...docSnap.data(),
          _isGeneratedTexture: true,
          type: 'texture',
          category: 'Generated Texture'
        });
      });
      setGeneratedTextures(textures);
    } catch (error) {
      console.error('Error fetching generated textures:', error);
    } finally {
      setTexturesLoading(false);
    }
  }, [authUser]);

  // Fetch user data for credit checking
  useEffect(() => {
    if (!authUser) return;
    const fetchUserData = async () => {
      const userRef = doc(db, 'users', authUser.uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setUserData(userSnap.data());
      }
    };
    fetchUserData();
  }, [authUser]);

  // Fetch assets on mount
  useEffect(() => {
    if (authUser) {
      fetchUserAssets();
      fetchGeneratedTextures();
    }
  }, [authUser, fetchUserAssets, fetchGeneratedTextures]);

  // Sync external assets if provided
  useEffect(() => {
    if (externalUserAssets && JSON.stringify(externalUserAssets) !== JSON.stringify(userAssets)) {
      setUserAssets(externalUserAssets);
    }
  }, [externalUserAssets]);

  // ============================================
  // HANDLE ASSET UPLOAD
  // ============================================
  const handleAssetUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isImage = ALLOWED_IMAGE_TYPES.includes(file.type);
    const is3D = ALLOWED_3D_TYPES.includes(fileExtension);

    if (!isImage && !is3D) {
      alert('Only images (JPEG, PNG, WEBP, GIF) and 3D files (OBJ, GLTF, GLB) are allowed.');
      e.target.value = '';
      return;
    }

    const maxSize = is3D ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert(`File too large. Maximum size is ${is3D ? '50MB' : '10MB'}.`);
      e.target.value = '';
      return;
    }

    // Check storage credits before upload
    const fileSizeMB = file.size / (1024 * 1024);
    const uploadCost = calculateAssetUploadCost({
      fileSizeMB: fileSizeMB,
      assetType: is3D ? '3d' : 'image'
    });
    
    const costCheck = checkSufficientCredits(userData, uploadCost, 'image');
    if (!costCheck.hasCredits) {
      alert(`Insufficient credits for upload. Cost: ${uploadCost}, Available: ${costCheck.remaining}`);
      e.target.value = '';
      return;
    }

    setUploadingAsset(true);
    setUploadProgress(0);

    try {
      const storage = getStorage();
      const timestamp = Date.now();
      const safeFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storagePath = `users/${authUser.uid}/assets/${timestamp}_${safeFileName}`;
      const storageRef = ref(storage, storagePath);

      const reader = new FileReader();
      const dataUrl = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      await uploadString(storageRef, dataUrl, 'data_url');
      clearInterval(progressInterval);
      setUploadProgress(100);

      const downloadURL = await getDownloadURL(storageRef);

      const assetData = {
        userId: authUser.uid,
        name: file.name,
        type: is3D ? '3d' : 'image',
        category: is3D ? '3D Model' : 'Image',
        fileSize: file.size,
        fileExtension: fileExtension,
        downloadURL: downloadURL,
        thumbnailURL: isImage ? downloadURL : null,
        dimensions: null,
        createdAt: serverTimestamp(),
      };

      // Deduct storage credits (using image credits as proxy)
      const deductResult = await deductImageCredits(
        authUser.uid, 
        uploadCost, 
        { type: is3D ? '3d_upload' : 'image_upload' }
      );
      
      if (deductResult.success && updateRemainingCounts) {
        updateRemainingCounts();
      }

      await fetchUserAssets();

      setUploadingAsset(false);
      setUploadProgress(0);

    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload asset. Please try again.');
      setUploadingAsset(false);
      setUploadProgress(0);
    }

    e.target.value = '';
  };

  // ============================================
  // HANDLE DELETE ASSET
  // ============================================
  const handleDeleteAsset = async () => {
    if (!assetToDelete || !authUser) return;

    try {
      const assetRef = doc(db, 'assets', assetToDelete.id);
      await deleteDoc(assetRef);

      try {
        const storage = getStorage();
        const storageRef = ref(storage, `users/${authUser.uid}/assets/${assetToDelete.name}`);
        await deleteObject(storageRef);
      } catch (storageError) {
        console.warn('Could not delete from storage:', storageError);
      }

      setUserAssets(prev => prev.filter(a => a.id !== assetToDelete.id));

      setShowDeleteAssetPopup(false);
      setAssetToDelete(null);
      if (showAssetDetails && selectedAsset?.id === assetToDelete.id) {
        setShowAssetDetails(false);
        setSelectedAsset(null);
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      alert('Failed to delete asset. Please try again.');
    }
  };

  // ============================================
  // HANDLE ADD TO PROJECT
  // ============================================
  const handleAddToProject = (asset) => {
    if (onAddToProject) {
      onAddToProject(asset);
    } else {
      const pendingAsset = {
        id: asset.id,
        name: asset.name,
        type: asset.type,
        downloadURL: asset.downloadURL,
        fileExtension: asset.fileExtension,
        thumbnailURL: asset.thumbnailURL,
      };
      sessionStorage.setItem('pendingAsset', JSON.stringify(pendingAsset));
      window.location.href = '/editor';
    }
  };

  // ============================================
  // COMBINE AND FILTER ASSETS
  // ============================================
  // Combine uploaded assets and generated textures
  const allAssets = useMemo(() => {
    const uploaded = userAssets.map(asset => ({ ...asset, source: 'upload' }));
    const textures = generatedTextures.map(texture => ({ 
      ...texture, 
      source: 'generated',
      fileSize: 0,
      fileExtension: 'png'
    }));
    return [...uploaded, ...textures];
  }, [userAssets, generatedTextures]);
  
  const filteredAssets = allAssets.filter(asset => {
    if (filterType === 'all') return true;
    if (filterType === 'image') return asset.type === 'image' && asset.source !== 'generated';
    if (filterType === '3d') return asset.type === '3d';
    if (filterType === 'textures') return asset.type === 'texture' || asset._isGeneratedTexture === true;
    return true;
  });

  // ============================================
  // RENDER COMPONENT
  // ============================================
  return (
    <>
      {/* INLINE STYLES FOR ANIMATIONS */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes shimmer {
          0% { background-position: -400px 0; }
          100% { background-position: 400px 0; }
        }
        
        /* Mobile Styles */
        @media (max-width: 767px) {
          .asset-library-overlay {
            padding: 0 !important;
            background-color: white !important;
            backdrop-filter: none !important;
            align-items: flex-start !important;
          }
          .asset-library-panel-container {
            width: 100% !important;
            height: 100vh !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
            background: white !important;
          }
          .asset-library-header {
            padding: 12px 16px !important;
            position: sticky !important;
            top: 0 !important;
            background: white !important;
            z-index: 20 !important;
            border-bottom: 1px solid #eaeaea !important;
          }
          .asset-library-header h2 {
            font-size: 1.2rem !important;
          }
          .asset-library-content-scroll {
            padding: 16px 16px 24px 16px !important;
          }
          .asset-grid-mobile {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        
        /* Desktop styles */
        @media (min-width: 768px) {
          .asset-library-header h2 {
            font-size: 1.6rem !important;
          }
        }
      `}</style>
      
      {/* OVERLAY CONTAINER */}
      <div 
        className="asset-library-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 100,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }} 
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        {/* PANEL CONTAINER */}
        <div 
          className="asset-library-panel-container"
          style={{
            backgroundColor: 'white',
            borderRadius: '24px',
            width: '100%',
            maxWidth: '1400px',
            height: '90vh',
            maxHeight: 'calc(100vh - 40px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
          }}
        >
          
          {/* MAIN CONTENT */}
          <div className="asset-library-content" style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            background: 'white',
            overscrollBehavior: 'none',
            touchAction: 'pan-y',
            WebkitOverflowScrolling: 'touch',
          }}>
            
                        {/* Header - STICKY */}
            <div className="asset-library-header" style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'flex-start',
              flexShrink: 0, 
              position: 'sticky',
              top: 0,
              background: 'white',
              zIndex: 20,
              padding: '16px 24px 12px 24px',
              borderBottom: '1px solid #eaeaea',
              gap: '8px',
            }}>
              <button
                onClick={onClose}
                style={{
                  background: 'rgba(217, 217, 217, 1)',
                  color: '#ffffffff',
                  padding: '6px 16px',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontFamily: 'Avenir, sans-serif',
                  fontSize: '0.8rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: 'none',
                  flexShrink: 0,
                }}
              >
                <FiArrowLeft size={14} />
                Back
              </button>
              <h2 style={{
                fontFamily: 'Avenir, sans-serif',
                fontWeight: 600,
                fontSize: '1.6rem',
                color: '#333',
                letterSpacing: '-0.3px',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <TbCube3dSphere size={28} />
                Asset Library
              </h2>
            </div>
            
            {/* Content wrapper */}
            <div className="asset-library-content-scroll" style={{ padding: '24px 24px 32px 24px' }}>
              
              {/* Upload Progress Bar */}
              {uploadingAsset && (
                <div style={{
                  marginBottom: '16px',
                  background: '#f0f0f0',
                  borderRadius: '8px',
                  padding: '12px',
                  flexShrink: 0,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                    <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#666' }}>Uploading...</span>
                    <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#666' }}>{uploadProgress}%</span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '4px',
                    background: '#ddd',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${uploadProgress}%`,
                      height: '100%',
                      background: '#88b7ff',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
              
              {/* Filter Bar */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                flexShrink: 0,
                gap: '12px',
              }}>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: '1px solid #ddd',
                      background: 'white',
                      color: '#333',
                      cursor: 'pointer',
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {filterType === 'all' ? 'All' :
                     filterType === 'images' ? 'Images' :
                     filterType === '3d' ? '3D Models' :
                     'Textures'}
                    <FiChevronDown size={14} />
                  </button>
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      minWidth: '150px',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      overflow: 'hidden',
                      maxHeight: showFilterDropdown ? '200px' : '0',
                      opacity: showFilterDropdown ? 1 : 0,
                      transition: 'max-height 0.3s ease, opacity 0.2s ease',
                      zIndex: 10,
                      border: showFilterDropdown ? '1px solid #eaeaea' : 'none',
                    }}
                  >
                    {['all', 'images', '3d', 'textures'].map((filter) => (
                      <button
                        key={filter}
                        onClick={() => {
                          setFilterType(filter);
                          setShowFilterDropdown(false);
                        }}
                        style={{
                          padding: '10px 16px',
                          width: '100%',
                          textAlign: 'left',
                          border: 'none',
                          background: filterType === filter ? '#f0f4ff' : 'white',
                          color: filterType === filter ? '#4594F9' : '#333',
                          cursor: 'pointer',
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.85rem',
                          transition: 'background 0.2s ease',
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = '#f5f5f5'}
                        onMouseLeave={(e) => {
                          if (filterType !== filter) e.currentTarget.style.background = 'white';
                          else e.currentTarget.style.background = '#f0f4ff';
                        }}
                      >
                        {filter === 'all' ? 'All' :
                         filter === 'images' ? 'Images' :
                         filter === '3d' ? '3D Models' :
                         'Textures'}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => assetFileInputRef.current?.click()}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '20px',
                    border: 'none',
                    background: '#88b7ff',
                    color: 'white',
                    cursor: 'pointer',
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.85rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    flexShrink: 0,
                  }}
                >
                  <FiPlus size={14} /> Upload Asset
                </button>
              </div>
              
              {/* Asset Grid or Empty State */}
              {assetsLoading ? (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <SkeletonShimmer width="180px" height="180px" borderRadius="12px" />
                  <SkeletonShimmer width="180px" height="180px" borderRadius="12px" style={{ opacity: 0.6 }} />
                </div>
              ) : filteredAssets.length === 0 ? (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#f8f8f8',
                  borderRadius: '16px',
                  padding: '60px 20px',
                }}>
                  <TbCube3dSphere size={64} color="#ccc" />
                  <h3 style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontWeight: 600,
                    fontSize: '1.2rem',
                    color: '#999',
                    margin: '16px 0 8px 0',
                  }}>
                    Your Asset Library is Empty
                  </h3>
                  <p style={{
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.9rem',
                    color: '#bbb',
                    marginBottom: '24px',
                    textAlign: 'center',
                    maxWidth: '400px',
                  }}>
                    Upload images and 3D files to use in your projects. Supported formats: JPEG, PNG, WEBP, GIF, OBJ, GLTF, GLB
                  </p>
                  <button
                    onClick={() => assetFileInputRef.current?.click()}
                    style={{
                      padding: '12px 24px',
                      background: '#88b7ff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '30px',
                      cursor: 'pointer',
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '1rem',
                      fontWeight: 500,
                    }}
                  >
                    <FiPlus size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                    Upload Your First Asset
                  </button>
                </div>
              ) : (
                <div className="asset-grid-mobile" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '16px',
                  flex: 1,
                  alignContent: 'start',
                }}>
                  {filteredAssets.map((asset) => (
                    <div
                      key={asset.id}
                      onClick={() => {
                        setSelectedAsset(asset);
                        setShowAssetDetails(true);
                      }}
                      style={{
                        borderRadius: '12px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '1px solid #eaeaea',
                        background: 'white',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'none';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{
                        aspectRatio: '1/1',
                        background: asset.type === 'image' ? '#f5f5f5' : '#e8e8ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                      }}>
                        {asset.type === 'image' && asset.thumbnailURL ? (
                          <LazyImage
                            src={asset.thumbnailURL}
                            alt={asset.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                        ) : (
                          <div style={{ color: '#999', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            {getAssetIcon(asset)}
                            <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.7rem', color: '#aaa' }}>
                              {asset.fileExtension?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssetToDelete(asset);
                            setShowDeleteAssetPopup(true);
                          }}
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            background: '#e02f1d',
                            border: 'none',
                            borderRadius: '50%',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: 'white',
                            padding: 0,
                            zIndex: 5,
                            transition: 'all 0.2s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#bb291fff';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#e02f1d';
                          }}
                        >
                          <FaRegTrashCan size={14} />
                        </button>
                        {asset.type === '3d' && (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              setMiniViewerAsset(asset);
                              setShowMiniViewer(true);
                            }}
                            style={{
                              position: 'absolute',
                              bottom: '8px',
                              left: '8px',
                              background: 'rgba(0,0,0,0.6)',
                              borderRadius: '10px',
                              padding: '4px 8px',
                              fontSize: '0.6rem',
                              color: 'white',
                              fontFamily: 'Avenir, sans-serif',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <TbCube3dSphere size={12} />
                            Preview
                          </div>
                        )}
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          background: asset.type === '3d' ? '#7c5cfc' : '#88b7ff',
                          borderRadius: '10px',
                          padding: '2px 8px',
                          fontSize: '0.65rem',
                          color: 'white',
                          fontFamily: 'Avenir, sans-serif',
                        }}>
                          {asset.type === '3d' ? '3D' : 'IMG'}
                        </div>
                      </div>
                      <div style={{ padding: '12px' }}>
                        <div style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          color: '#333',
                          marginBottom: '4px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}>
                          {asset.name}
                        </div>
                        <div style={{
                          fontFamily: 'Avenir, sans-serif',
                          fontSize: '0.7rem',
                          color: '#999',
                        }}>
                          {formatFileSize(asset.fileSize)} • {asset.fileExtension?.toUpperCase()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Hidden File Input */}
      <input
        type="file"
        ref={assetFileInputRef}
        accept="image/jpeg,image/png,image/webp,image/gif,.obj,.gltf,.glb"
        style={{ display: 'none' }}
        onChange={handleAssetUpload}
      />
      
      {/* ASSET DETAIL VIEW MODAL */}
      {showAssetDetails && selectedAsset && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.8)',
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }} onClick={() => {
          setShowAssetDetails(false);
          setSelectedAsset(null);
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }} onClick={(e) => e.stopPropagation()}>
            
            {/* Detail Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px 20px',
              borderBottom: '1px solid #eee',
            }}>
              <h3 style={{
                fontFamily: 'Avenir, sans-serif',
                fontWeight: 600,
                fontSize: '1.1rem',
                color: '#333',
                margin: 0,
              }}>
                Asset Details
              </h3>
              <button
                onClick={() => {
                  setShowAssetDetails(false);
                  setSelectedAsset(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '4px',
                  borderRadius: '50%',
                }}
              >
                <FiX size={20} />
              </button>
            </div>
            
            {/* Detail Content - Two Column Layout */}
            <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
              {/* Left - Asset Preview */}
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#f5f5f5',
                padding: '20px',
                minHeight: '300px',
              }}>
                {selectedAsset.type === 'image' && selectedAsset.thumbnailURL ? (
                  <LazyImage
                    src={selectedAsset.thumbnailURL}
                    alt={selectedAsset.name}
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      borderRadius: '8px',
                      objectFit: 'contain',
                    }}
                  />
                ) : selectedAsset.type === '3d' ? (
                  <div style={{ width: '100%', height: '100%', minHeight: '350px' }}>
                    <MiniModelViewer
                      assetUrl={selectedAsset.downloadURL}
                      fileExtension={selectedAsset.fileExtension}
                      onClose={() => {
                        setShowAssetDetails(false);
                        setSelectedAsset(null);
                      }}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: '#999' }}>
                    {getAssetIcon(selectedAsset)}
                    <p style={{
                      fontFamily: 'Avenir, sans-serif',
                      marginTop: '12px',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      color: '#666'
                    }}>
                      {selectedAsset.name}
                    </p>
                    <p style={{
                      fontFamily: 'Avenir, sans-serif',
                      fontSize: '0.85rem',
                      color: '#999'
                    }}>
                      {selectedAsset.fileExtension?.toUpperCase()}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Right - Details Sidebar */}
              <div style={{
                width: '350px',
                borderLeft: '1px solid #e0e0e0',
                background: 'white',
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                flexShrink: 0,
              }}>
                <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Asset Info */}
                  <div>
                    <h4 style={{
                      fontFamily: 'Avenir, sans-serif',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      color: '#333',
                      marginBottom: '10px',
                    }}>
                      Information
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Name</span>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{selectedAsset.name}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Type</span>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333', textTransform: 'capitalize' }}>
                          {selectedAsset.type === '3d' ? '3D Model' : 'Image'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Format</span>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>{selectedAsset.fileExtension?.toUpperCase()}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Size</span>
                        <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>{formatFileSize(selectedAsset.fileSize)}</span>
                      </div>
                      {selectedAsset.createdAt && (
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.8rem', color: '#888' }}>Uploaded</span>
                          <span style={{ fontFamily: 'Avenir, sans-serif', fontSize: '0.85rem', color: '#333' }}>
                            {selectedAsset.createdAt.toDate ?
                              new Date(selectedAsset.createdAt.toDate()).toLocaleDateString() :
                              'Just now'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
                    <button
                      onClick={() => handleAddToProject(selectedAsset)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 16px',
                        background: '#88b7ff',
                        color: 'white',
                        borderRadius: '12px',
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'Avenir, sans-serif',
                        fontSize: '0.9rem',
                        fontWeight: 500,
                      }}
                    >
                      <FiPlus size={18} />
                      Add to Project
                    </button>
                    <a
                      href={selectedAsset.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={selectedAsset.name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 16px',
                        background: 'white',
                        color: '#333',
                        borderRadius: '12px',
                        border: '1px solid #e0e0e0',
                        cursor: 'pointer',
                        fontFamily: 'Avenir, sans-serif',
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                      }}
                    >
                      <FiDownload size={18} />
                      Export Asset
                    </a>
                    <button
                      onClick={() => {
                        setAssetToDelete(selectedAsset);
                        setShowDeleteAssetPopup(true);
                        setShowAssetDetails(false);
                        setSelectedAsset(null);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '12px 16px',
                        border: '1px solid #ffcccc',
                        borderRadius: '12px',
                        background: 'white',
                        cursor: 'pointer',
                        fontFamily: 'Avenir, sans-serif',
                        fontSize: '0.9rem',
                        color: '#e02f1d',
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fff5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                      }}
                    >
                      <FaRegTrashCan size={18} />
                      Delete Asset
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* DELETE ASSET CONFIRMATION POPUP */}
      {showDeleteAssetPopup && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 300,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} onClick={() => setShowDeleteAssetPopup(false)}>
          <div style={{
            backgroundColor: '#FAFAFA', borderRadius: '16px', padding: '25px',
            maxWidth: '400px', width: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#333', marginBottom: '16px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '1.1rem' }}>Delete Asset</h3>
            <p style={{ color: '#666', marginBottom: '24px', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.95rem', lineHeight: 1.5 }}>
              This asset will be permanently deleted from your account. Are you sure you want to continue?
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowDeleteAssetPopup(false);
                  setAssetToDelete(null);
                }}
                style={{
                  padding: '10px 20px', backgroundColor: 'transparent', border: '1px solid #ccc',
                  borderRadius: '30px', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
                }}
              >
                No, Cancel
              </button>
              <button
                onClick={handleDeleteAsset}
                style={{
                  padding: '10px 20px', backgroundColor: '#e02f1d', border: 'none',
                  borderRadius: '30px', color: 'white', cursor: 'pointer', fontFamily: 'avenir-lt-pro, sans-serif'
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* MINI 3D VIEWER MODAL */}
      {showMiniViewer && miniViewerAsset && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          
          height: 'calc(var(--vh, 1vh) * 100)',
          background: 'rgba(0, 0, 0, 0.8)',
          zIndex: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
          backdropFilter: 'blur(8px)',
        }} onClick={() => {
          setShowMiniViewer(false);
          setMiniViewerAsset(null);
        }}>
          <div style={{
            background: '#2a2a2a',
            borderRadius: '16px',
            overflow: 'hidden',
            width: '90%',
            maxWidth: '500px',
            height: '450px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '12px 16px',
              background: '#333',
              color: 'white',
              fontFamily: 'Avenir, sans-serif',
              flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <TbCube3dSphere size={18} />
                <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{miniViewerAsset.name}</span>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => handleAddToProject(miniViewerAsset)}
                  style={{
                    background: '#88b7ff',
                    border: 'none',
                    borderRadius: '16px',
                    padding: '6px 14px',
                    color: 'white',
                    cursor: 'pointer',
                    fontFamily: 'Avenir, sans-serif',
                    fontSize: '0.75rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <FiPlus size={12} /> Add to Project
                </button>
                <button
                  onClick={() => {
                    setShowMiniViewer(false);
                    setMiniViewerAsset(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#aaa',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '50%',
                  }}
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <MiniModelViewer
                assetUrl={miniViewerAsset.downloadURL}
                fileExtension={miniViewerAsset.fileExtension}
                onClose={() => {
                  setShowMiniViewer(false);
                  setMiniViewerAsset(null);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AssetLibraryPanel;